import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Play, PenTool, Monitor, Users, Clapperboard, ChevronRight, GraduationCap } from 'lucide-react';

const AnimationStudio: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "PixelMotion Institute";
    
    return (
        <div className="min-h-screen bg-[#111] font-sans text-slate-200 selection:bg-[#ff0055] selection:text-white">
            {/* Top Navigation */}
            <nav className="fixed w-full z-50 bg-[#111]/90 backdrop-blur-md border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#ff0055] rounded-xl flex items-center justify-center text-white shadow-lg shadow-pink-900/20">
                            <Clapperboard size={20} />
                        </div>
                        <span className="font-bold text-xl tracking-wide text-white">{schoolName}</span>
                    </div>
                    
                    <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
                        <a href="#" className="hover:text-white transition-colors">Programs</a>
                        <a href="#" className="hover:text-white transition-colors">Student Gallery</a>
                        <a href="#" className="hover:text-white transition-colors">Faculty</a>
                        <a href="#" className="hover:text-white transition-colors">Admissions</a>
                    </div>

                    <div className="flex gap-4">
                        <Button variant="ghost" className="text-white hover:bg-white/10">
                            Login
                        </Button>
                        <Button className="bg-white text-black hover:bg-slate-200 font-bold rounded-full px-6">
                            Apply Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="pt-32 pb-20 px-6 max-w-7xl mx-auto relative overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[#ff0055] text-xs font-bold uppercase tracking-widest mb-8">
                            <span className="w-2 h-2 rounded-full bg-[#ff0055] animate-pulse"></span> Admissions Open 2026
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 tracking-tight">
                            Create Worlds. <br/>
                            <span className="text-[#ff0055]">Animate Life.</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-10 max-w-lg leading-relaxed">
                            The premier destination for aspiring animators and VFX artists. Master industry-standard tools and bring your stories to the screen.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Button className="bg-[#ff0055] hover:bg-[#d40047] text-white font-bold rounded-lg px-8 h-12 text-base shadow-lg shadow-pink-900/20">
                                View Programs
                            </Button>
                            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 font-bold rounded-lg px-8 h-12 text-base gap-2">
                                <Play size={16} fill="currentColor" /> Watch Showreel
                            </Button>
                        </div>
                    </div>

                    {/* Hero Art/Video Placeholder */}
                    <div className="relative aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10 group cursor-pointer">
                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/40 to-orange-900/40 mix-blend-overlay"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white border border-white/20 group-hover:scale-110 transition-transform">
                                <Play size={32} fill="white" className="ml-1" />
                            </div>
                        </div>
                         {/* Abstract Shapes for Placeholder */}
                        <div className="absolute top-10 left-10 w-20 h-20 bg-[#ff0055] rounded-full blur-[50px] opacity-50"></div>
                        <div className="absolute bottom-10 right-10 w-32 h-32 bg-blue-500 rounded-full blur-[60px] opacity-40"></div>
                    </div>
                </div>
            </header>

            {/* Content / Programs Section */}
            <section className="py-24 bg-[#0a0a0a] border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex justify-between items-end mb-16">
                        <div>
                            <h2 className="text-3xl font-bold text-white mb-4">Academic Programs</h2>
                            <p className="text-slate-400 max-w-xl">
                                Comprehensive curriculum covering 2D, 3D, and VFX.
                            </p>
                        </div>
                        <a href="#" className="hidden md:flex items-center gap-2 text-[#ff0055] font-bold text-sm hover:underline">
                            Download Catalog <ChevronRight size={16} />
                        </a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: PenTool, title: "2D Animation", desc: "Traditional hand-drawn and digital puppet animation techniques." },
                            { icon: Monitor, title: "3D Character Animation", desc: "Advanced character mechanics, acting, and performance." },
                            { icon: Clapperboard, title: "Visual Effects (VFX)", desc: "Compositing, simulation, and integration for film and TV." }
                        ].map((item, i) => (
                            <div key={i} className="bg-[#161616] p-8 rounded-2xl border border-white/5 hover:border-[#ff0055]/50 hover:bg-[#1a1a1a] transition-all group">
                                <div className="w-14 h-14 bg-[#222] rounded-xl flex items-center justify-center text-white mb-6 group-hover:bg-[#ff0055] transition-colors">
                                    <item.icon size={26} />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                                <p className="text-slate-400 leading-relaxed mb-6 text-sm">{item.desc}</p>
                                <span className="text-xs font-bold text-white/50 uppercase tracking-widest group-hover:text-white transition-colors">
                                    4 Year BFA
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Stats/Employment */}
            <section className="py-24 px-6 max-w-7xl mx-auto">
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                     <div className="md:col-span-2">
                         <h2 className="text-3xl font-bold text-white mb-6">Career Focused Education</h2>
                         <p className="text-slate-400 text-lg leading-relaxed mb-8">
                             Our graduates are working at top studios around the world. We provide dedicated career support, portfolio reviews, and internship placements.
                         </p>
                         <Button variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black">
                             See Alumni Success Stories
                         </Button>
                     </div>
                     <div className="bg-[#161616] p-8 rounded-2xl border border-white/5 flex flex-col justify-center text-center">
                         <div className="text-5xl font-bold text-[#ff0055] mb-2">94%</div>
                         <div className="text-sm text-slate-400 uppercase tracking-widest font-bold">Industry Placement</div>
                     </div>
                     <div className="bg-[#161616] p-8 rounded-2xl border border-white/5 flex flex-col justify-center text-center">
                         <div className="text-5xl font-bold text-white mb-2">12+</div>
                         <div className="text-sm text-slate-400 uppercase tracking-widest font-bold">Partner Studios</div>
                     </div>
                 </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#050505] py-16 px-6 border-t border-white/5">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
                    <div className="flex items-center gap-3">
                        <Clapperboard size={24} className="text-[#ff0055]" />
                        <span className="font-bold text-white text-lg">{schoolName}</span>
                    </div>
                    <div className="flex gap-8 text-sm text-slate-500">
                        <a href="#" className="hover:text-white transition-colors">Terms</a>
                        <a href="#" className="hover:text-white transition-colors">Privacy</a>
                        <a href="#" className="hover:text-white transition-colors">Cookies</a>
                    </div>
                    <div className="text-slate-600 text-sm">
                        &copy; 2026 {schoolName}.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AnimationStudio;
